import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useIsFetching,
  useMutation,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { http } from '~/apis/base';
import { QueryKeyEnforcement } from '~/apis/enforcement/reporting';
import { MonitoringReviewNeeded } from '~/swagger/MonitoringReviewNeeded';
import {
  MonitoringPotentialAuthorizedProtocolsGetAllSellerIdsRequest,
  MonitoringPotentialAuthorizedProtocolsGetAllSellerIdsResponse,
  MonitoringPotentialAuthorizedProtocolsGetCountRequest,
  MonitoringPotentialAuthorizedProtocolsGetCountResponse,
  MonitoringPotentialAuthorizedProtocolsGetListingsRequest,
  MonitoringPotentialAuthorizedProtocolsGetListingsResponse,
  MonitoringPotentialAuthorizedProtocolsGetSellersRequest,
  MonitoringPotentialAuthorizedProtocolsGetSellersResponse,
} from '~/swagger/data-contracts';
import { QUERY_KEY_FILTER } from '~/apis/@constants';
import { queryClient } from '..';

export const ReviewNeededQueryKey = 'review-needed';
const QueryOptions = {
  refetchOnWindowFocus: false,
};

export const reviewNeededApi = new MonitoringReviewNeeded(http);

export const useGetReviewNeededCountQuery = (
  request: MonitoringPotentialAuthorizedProtocolsGetCountRequest
): UseQueryResult<MonitoringPotentialAuthorizedProtocolsGetCountResponse, unknown> => {
  return useQuery(
    [ReviewNeededQueryKey, 'review-needed-count', request],
    async () => {
      const { data } = await reviewNeededApi.reviewNeededPotentialAuthorizedCountCreate(request);

      return data;
    },
    QueryOptions
  );
};

export const useGetReviewNeededSellerListQuery = (
  request: MonitoringPotentialAuthorizedProtocolsGetSellersRequest,
  options?: UseInfiniteQueryOptions<MonitoringPotentialAuthorizedProtocolsGetSellersResponse>
) => {
  return useInfiniteQuery<MonitoringPotentialAuthorizedProtocolsGetSellersResponse>(
    [ReviewNeededQueryKey, 'review-needed-seller-list', request],
    async ({ pageParam }) => {
      const { data } = await reviewNeededApi.reviewNeededPotentialAuthorizedSellersCreate({
        ...request,
        offset: pageParam || request.offset,
      });

      return data;
    },
    {
      ...options,
      getNextPageParam: (lastPage, allPage) => {
        if (lastPage.sellers.length < 50) {
          return;
        }

        return request.limit ? request.limit * allPage.length : 0;
      },
    }
  );
};

export const useGetReviewNeededListingsQuery = (
  request: MonitoringPotentialAuthorizedProtocolsGetListingsRequest
): UseQueryResult<MonitoringPotentialAuthorizedProtocolsGetListingsResponse, unknown> => {
  return useQuery([ReviewNeededQueryKey, 'review-needed-listings', request], async () => {
    const { data } = await reviewNeededApi.reviewNeededPotentialAuthorizedListingsCreate(request);

    return data;
  });
};

const invalidateReviewNeededListingQueries = () => {
  return Promise.all([
    queryClient.invalidateQueries([ReviewNeededQueryKey]),
    queryClient.invalidateQueries([QueryKeyEnforcement]),
    queryClient.invalidateQueries([QUERY_KEY_FILTER]),
  ]);
};

export const useReviewNeededMarkAsAuthorizedMutation = () => {
  return useMutation(reviewNeededApi.reviewNeededPotentialAuthorizedMarkAsAuthorizedCreate, {
    onSuccess: async () => {
      await invalidateReviewNeededListingQueries();
    },
  });
};

export const useReviewNeededMarkAsUnauthorizedMutation = () => {
  return useMutation(reviewNeededApi.reviewNeededPotentialAuthorizedMarkAsUnauthorizedCreate, {
    onSuccess: async () => {
      await invalidateReviewNeededListingQueries();
    },
  });
};

export const useReviewNeededDismissMutation = () => {
  return useMutation(reviewNeededApi.reviewNeededPotentialAuthorizedMarkAsDismissedCreate, {
    onSuccess: async () => {
      await invalidateReviewNeededListingQueries();
    },
  });
};

// #region Fetch Review Needed Select All Id List Query
const reviewNeededSelectAllIdListQueryFn = async (
  params: MonitoringPotentialAuthorizedProtocolsGetAllSellerIdsRequest
) => {
  const { data } = await reviewNeededApi.reviewNeededPotentialAuthorizedAllSellerIdsCreate(params);

  return data;
};
export const fetchReviewNeededSelectAllIdListQuery = (
  params: MonitoringPotentialAuthorizedProtocolsGetAllSellerIdsRequest,
  options?: UseQueryOptions<MonitoringPotentialAuthorizedProtocolsGetAllSellerIdsResponse>
) => {
  return queryClient.fetchQuery({
    queryKey: [ReviewNeededQueryKey, 'review-needed-select-all-id-list', params],
    queryFn: () => reviewNeededSelectAllIdListQueryFn(params),
    ...options,
  });
};
export const useReviewNeededSelectAllIsFetching = (
  params: MonitoringPotentialAuthorizedProtocolsGetAllSellerIdsRequest
) => {
  const queryCountInFetching = useIsFetching({
    queryKey: [ReviewNeededQueryKey, 'review-needed-select-all-id-list', params],
  });

  return queryCountInFetching > 0;
};

/**
 * @deprecated 서버 자원을 바꾸는 api가 아니므로 위의 fetchQuery + useIsFetching 사용권장
 */
export const useReviewNeededSelectAllIdListMutate = () => {
  return useMutation(reviewNeededApi.reviewNeededPotentialAuthorizedAllSellerIdsCreate);
};
// #endregion
